import {
  ChangeEvent,
  FocusEvent,
  Fragment,
  InputHTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  Ref,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import type { HTMLProps } from '@base-ui/react';
import { Popover } from '@base-ui/react/popover';
import { format, getMonth, getYear, isValid, parse, setMonth, setYear, startOfDay } from 'date-fns';
import styled from 'styled-components';

import { mergeRefs, useAriaId } from '@components/component-helpers';
import { FormError } from '@components/form-error';
import { FieldDescription, FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDate } from '@components/icons/date';
import { IconFormIndicatorLocked } from '@components/icons/form-indicator-locked';
import { MaskedInput } from '@components/masked-input';
import createAutoCorrectedDatePipe from '@components/masked-input/pipe/create-auto-corrected-date-pipe';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';
import { theme } from '@styles/theme';
import { isMobileDevice } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { dateFnsLocales } from '@utils/date';
import { transitionStandard } from '@utils/transitions';

import { CalendarBody } from './calendar-body';
import { clampToRange, toDate } from './helpers';
import { CalendarBackdrop, CalendarPopup, CalendarPositioner, CalendarSurface } from './styles';
import {
  CalendarView,
  ClearableDateChangeHandler,
  DatePickerProps,
  InlineDatePickerProps,
  InternalDatePickerProps,
  LanguageOptions,
} from './types';

// Sidebar's outside-click handler ignores clicks inside an element marked with
// this data attribute (see sidebar/sidebar.tsx), so clicking the calendar from
// inside a sidebar doesn't close it. A data attribute rather than an id lets
// multiple open popovers carry it without colliding.
export const DATEPICKER_PORTAL_ATTR = 'data-datepicker-portal';

// MaskedInput's render callback receives the standard input attributes plus
// an `onTextChange` callback that we strip before forwarding to the DOM input.
type MaskedInputRenderProps = InputHTMLAttributes<HTMLInputElement> & {
  onTextChange?: (text: string) => void;
};

const DEFAULT_DATE_FORMAT = 'dd / MM / yyyy';
// The typable input's mask and auto-correct pipe are fixed to
// DEFAULT_DATE_FORMAT's shape; keep the three in sync.
const inputMask = [/\d/, /\d/, ' ', '/', ' ', /\d/, /\d/, ' ', '/', ' ', /\d/, /\d/, /\d/, /\d/];
// Window after popover close / pointer-down within which input focus should
// not re-open the popover. See `handleInputFocus` below for the full story.
const FOCUS_REOPEN_GUARD_MS = 150;

// The clear (X) button renders only an icon, so it needs an explicit
// accessible name.
const clearButtonLabels: Record<LanguageOptions, string> = {
  en: 'Clear date',
  is: 'Hreinsa dagsetningu',
};

// The calendar popup carries role="dialog" (from base-ui) and would otherwise
// be an unnamed dialog; the APG date picker dialog pattern names it.
const calendarDialogLabels: Record<LanguageOptions, string> = {
  en: 'Choose date',
  is: 'Veldu dagsetningu',
};

export const DatePicker = (props: DatePickerProps) => {
  return <DatePickerImpl {...props} />;
};

export const InlineDatePicker = (props: InlineDatePickerProps) => {
  return <DatePickerImpl {...props} inline disableClear />;
};

const DatePickerImpl = (props: InternalDatePickerProps) => {
  const {
    value,
    defaultValue,
    onChange,
    disableClear,
    lang = 'en',
    error = '',
    dateFormat = DEFAULT_DATE_FORMAT,
    label,
    labelIcon,
    isDisabled,
    placeholder,
    hideLabel,
    customInput,
    inline,
    disablePastDates,
    disableFutureDates,
    minDate: minDateProp,
    maxDate: maxDateProp,
    description,
    pickerView = 'day',
    ...inputProps
  } = props;

  const today = startOfDay(new Date());
  const minDate = minDateProp ?? (disablePastDates ? today : undefined);
  const maxDate = maxDateProp ?? (disableFutureDates ? today : undefined);

  // Live auto-correction while typing: clamps each component to a sensible range
  // (month ≤ 12, day ≤ days-in-month, year within ±100) before the value reaches
  // handleInputChange. Pairs with — doesn't replace — the parse/min/max check on
  // commit. Only used by the typable (day-picker) input.
  const autoCorrectionPipe = createAutoCorrectedDatePipe('dd / mm / yyyy', {
    minYear: getYear(today) - 100,
    maxYear: getYear(today) + 100,
  });

  // Standard React convention: `value === undefined` means uncontrolled (we own
  // `localValue`); any other `value`, including `null`/`''`, means controlled.
  // Matters for clearing — a controlled parent that resets `value` to `null`/`''`
  // must blank the field, so we must NOT fall back to the last committed
  // `localValue` there.
  const isControlled = value !== undefined;
  const [localValue, setLocalValue] = useState<Date | undefined>(toDate(defaultValue));
  const selected = isControlled ? toDate(value) : localValue;

  const [open, setOpen] = useState(false);
  const [view, setView] = useState<CalendarView>(pickerView);
  // Bumped on blur when the input holds uncommitted text; the re-render alone
  // restores the display (see handleInputBlur below).
  const [, forceDisplayResync] = useState(0);
  const [viewingDate, setViewingDate] = useState<Date>(selected ?? new Date());
  // DayPicker stays in uncontrolled month mode so its internal keyboard focus
  // survives month-boundary navigation. Header clicks remount it with a new
  // defaultMonth by bumping this key; keyboard navigation only syncs viewingDate.
  const [dayPickerKey, setDayPickerKey] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // The calendar surface (popover popup, or the inline surface), so focus
  // queries are scoped to this picker's own calendar (multiple datepickers may
  // be mounted).
  const calendarRef = useRef<HTMLDivElement | null>(null);
  // The grid cell focus last sat on, so the Tab ring returns to it when cycling
  // back to the content stop.
  const lastContentCellRef = useRef<HTMLElement | null>(null);
  // Suppresses auto-open-on-focus right after the popover closes. When we close
  // from inside the calendar (day pick, Esc) handleOpenChange returns focus to
  // the input; without this guard that programmatic focus would immediately
  // reopen the popover we just closed.
  const lastClosedAt = useRef(0);
  // Used to suppress auto-open-on-focus when focus arrives via pointer.
  // Mouse clicks fire focus *before* click; Trigger's click handler toggles
  // open, so without this guard we'd open on focus and immediately close on
  // the click that follows. Keyboard focus has no preceding pointer event.
  const lastPointerDownAt = useRef(0);
  // Set when an arrow key opens the popover from a closed state: the popup
  // isn't in the DOM yet, so the entry focus runs from the popup's ref
  // callback (attachCalendarRef below) the moment it mounts — deterministic,
  // unlike a rAF, which can fire before or after the mount depending on timing.
  const pendingEntryFocus = useRef(false);

  // FormLabel's htmlFor needs a matching input id to associate the label with
  // the combobox (and make clicking the label focus the field); fall back to a
  // generated id when the consumer doesn't pass one, so the field is never an
  // unnamed combobox with a dangling label.
  const generatedId = useAriaId('datepicker');
  const inputId = inputProps.id ?? generatedId;

  const LabelWrapper = hideLabel ? VisuallyHidden : Fragment;

  // `onChange` is typed per `disableClear` (`Date | null`, or `Date`-only when
  // clearing is disabled), so we funnel through this single emitter and cast
  // once here instead of scattering casts at every call site.
  const emitChange = (date: Date | null) => {
    (onChange as ClearableDateChangeHandler | undefined)?.(date);
  };

  const commit = (date: Date) => {
    setLocalValue(date);
    // Keep the calendar's viewing month in sync with the committed value (e.g.
    // when a date is typed while the popover is open). Bump the DayPicker key
    // because its uncontrolled `defaultMonth` ignores prop changes after mount.
    setViewingDate(date);
    setDayPickerKey((k) => k + 1);
    emitChange(date);
  };

  const handleDaySelect = (date: Date | undefined, triggerDate: Date) => {
    // RDP passes undefined when the selected day is clicked again; use the
    // trigger date so that click still confirms the current selection.
    handleOpenChange(false);
    commit(date ?? triggerDate);
  };

  const handleViewSelect = (date: Date) => {
    // Picking at the configured commit level commits and closes; picking at a
    // higher level (e.g. year while in month picker) drills back down a step.
    if (view === pickerView) {
      commit(clampToRange(date, minDate, maxDate));
      handleOpenChange(false);
      return;
    }
    if (view === 'year') {
      setViewingDate(setYear(viewingDate, getYear(date)));
      setView('month');
      return;
    }
    if (view === 'month') {
      setViewingDate(setMonth(setYear(viewingDate, getYear(date)), getMonth(date)));
      setView('day');
    }
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setViewingDate(selected ?? new Date());
    } else {
      setView(pickerView);
      lastClosedAt.current = Date.now();
      // We disable base-ui's focus restore (finalFocus={false}), so on close we
      // return focus to the input ourselves — but only when focus is still
      // inside the calendar (a pick, or Esc from the grid). If focus already
      // left the field (Tab away), leave it where it landed.
      if (calendarRef.current?.contains(document.activeElement)) {
        inputRef.current?.focus();
      }
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value.trim();
    if (text === '') {
      // An emptied input is a clear only when clearing is enabled and there's
      // a value to clear — this is what keeps the `disableClear` `onChange`
      // honestly `Date`-only. With clearing disabled the committed value stays
      // put and the display resyncs on blur (see handleInputBlur). An invalid/
      // incomplete entry that the auto-correct pipe blanks out (e.g.
      // "99 / 99 / 9999") also lands here, and must not be mistaken for the
      // user clearing a committed date — that would spuriously fire onChange.
      if (!disableClear && selected) {
        setLocalValue(undefined);
        emitChange(null);
      }
      return;
    }
    const parsed = parse(text, dateFormat, new Date());
    if (!isValid(parsed)) return;
    // Compare by calendar day, matching react-day-picker's matchers (which use
    // differenceInCalendarDays, ignoring time-of-day). A raw timestamp compare
    // would reject a same-day typed date when minDate/maxDate carries a wall-clock
    // time: `parse` lands the typed value at midnight, so it reads as before
    // minDate even though the calendar lets you click that day.
    if (minDate && startOfDay(parsed) < startOfDay(minDate)) return;
    if (maxDate && startOfDay(parsed) > startOfDay(maxDate)) return;
    commit(parsed);
    // A complete, valid date was typed — close the popover so Tab moves to
    // the next form field rather than into the calendar.
    if (open) handleOpenChange(false);
  };

  const handleClear = (event: MouseEvent) => {
    event.stopPropagation();
    setLocalValue(undefined);
    emitChange(null);
  };

  const hasClearButton = !isDisabled && !!selected && !disableClear;

  const renderTrailingIcons = () => {
    if (isDisabled) {
      return (
        <TrailingArea>
          <IconFormIndicatorLocked />
        </TrailingArea>
      );
    }
    return (
      <TrailingArea>
        {hasClearButton && (
          <ClearAction>
            <IconButton
              size="sm"
              variant="ghost-danger"
              aria-label={clearButtonLabels[lang]}
              icon={<IconCloseRegular size={16} />}
              onClick={handleClear}
            />
          </ClearAction>
        )}
        <IconDate />
      </TrailingArea>
    );
  };

  const setViewingDateFromHeader = (date: Date) => {
    setViewingDate(date);
    if (view === 'day') setDayPickerKey((k) => k + 1);
  };

  // The content cell to land on when entering the calendar: the most relevant
  // one for the view (selected/today day, or highlighted month/year cell), else
  // the first enabled one. The calendar is out of the page tab order, so this —
  // wired to ArrowDown/ArrowUp and the Tab ring's content stop — is how focus
  // reaches the grid.
  const getCalendarEntryTarget = (): HTMLElement | null => {
    const root = calendarRef.current;
    if (!root) return null;
    const pick = (...selectors: string[]) => {
      for (const selector of selectors) {
        const el = root.querySelector<HTMLElement>(selector);
        if (el) return el;
      }
      return null;
    };
    // Every selector excludes disabled cells (the selected/today day or the
    // highlighted month can sit outside min/max): focus() on a disabled button
    // silently no-ops, so matching one would dead-end keyboard entry — the
    // first selector hit is returned and the enabled fallbacks never run.
    return view === 'day'
      ? pick(
          '.rdp-selected [data-testid="datepicker-day"]:not([disabled])',
          '.rdp-today [data-testid="datepicker-day"]:not([disabled])',
          '[data-testid="datepicker-day"]:not([data-outside]):not([disabled])'
        )
      : pick(
          `[data-testid="datepicker-${view}"][data-selected]:not([disabled])`,
          `[data-testid="datepicker-${view}"]:not([disabled])`
        );
  };

  // Selecting a month/year in the drill-down unmounts the focused grid cell;
  // base-ui's FocusManager then parks focus on the popup root (a div with no
  // key handlers), deadlocking keyboard nav. The same applies to an inline
  // calendar, where committing a day remounts the DayPicker (dayPickerKey
  // bump) and drops focus on <body> — and there's no popover close to hand
  // focus back to an input. Re-enter the new view's grid. Only when focus was
  // actually dropped — never steal it from the input or the header buttons,
  // and never on mount (an inline calendar must not grab focus by rendering).
  // Layout effect to win the same base-ui restore race as SyncFocusDayButton
  // (see calendar-body.tsx).
  const skipMountRecovery = useRef(true);
  useLayoutEffect(() => {
    if (skipMountRecovery.current) {
      skipMountRecovery.current = false;
      return;
    }
    if (!open && !inline) return;
    const active = document.activeElement;
    if (active !== calendarRef.current && active !== document.body) return;
    getCalendarEntryTarget()?.focus();
    // Keyed to view drill-downs and grid remounts only. open/inline/
    // getCalendarEntryTarget are read for the current render but deliberately
    // excluded — recovery should fire when the grid changes under focus, not
    // when those happen to change identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, dayPickerKey]);

  const calendar = (
    <CalendarBody
      view={view}
      viewingDate={viewingDate}
      dayPickerKey={dayPickerKey}
      selected={selected}
      lang={lang}
      minDate={minDate}
      maxDate={maxDate}
      // Arrow-key entry + Tab trap only apply to the popover; an inline calendar
      // has no input to arrow in from and stays normally tabbable.
      arrowNavMode={!inline}
      onDaySelect={handleDaySelect}
      onViewSelect={handleViewSelect}
      onViewChange={setView}
      onViewDateChange={setViewingDate}
      onHeaderViewDateChange={setViewingDateFromHeader}
    />
  );

  if (inline) {
    return (
      <Stack space={1}>
        <LabelWrapper>
          <FormLabel text={label} icon={labelIcon} />
        </LabelWrapper>
        <FieldDescription text={description} />
        <CalendarSurface ref={calendarRef} $inline>
          {calendar}
        </CalendarSurface>
        <FormError text={error} />
      </Stack>
    );
  }

  // On mobile, suppress the soft keyboard while the calendar is open so the
  // calendar gets full focus.
  const isMobileOpen = isMobileDevice && open;
  // Typing is only wired for day granularity in the default format — the mask
  // and auto-correct pipe are fixed to DEFAULT_DATE_FORMAT's shape, so text
  // typed against any other format could never parse back into a date. Month/
  // year pickers, custom formats, and mobile (calendar open) all fall back to
  // a readonly input.
  const isReadonlyInput =
    isMobileOpen || pickerView !== 'day' || dateFormat !== DEFAULT_DATE_FORMAT;

  // Open the popover when the input gets focus (e.g. Tab from another field).
  // Skip just after the popover closed (close-from-inside refocuses the input,
  // which would otherwise reopen it) and just after a pointerdown (the click
  // that follows drives the toggle via the Trigger). Go through handleOpenChange,
  // not a bare setOpen, so viewingDate resyncs to the selection — base-ui only
  // routes its own interactions through onOpenChange.
  const handleInputFocus = () => {
    if (open) return;
    const now = Date.now();
    if (now - lastClosedAt.current < FOCUS_REOPEN_GUARD_MS) return;
    if (now - lastPointerDownAt.current < FOCUS_REOPEN_GUARD_MS) return;
    handleOpenChange(true);
  };
  const handleInputFocusWithPassthrough = (event: FocusEvent<HTMLInputElement>) => {
    inputProps.onFocus?.(event);
    handleInputFocus();
  };
  const handleInputPointerDown = (event: PointerEvent<HTMLInputElement>) => {
    inputProps.onPointerDown?.(event);
    lastPointerDownAt.current = Date.now();
  };

  // base-ui's FocusManager renders focus guards (`data-base-ui-focus-guard`,
  // tabIndex 0) at the end of <body>. With the calendar out of the tab order, a
  // native Tab from the input would land on a guard and be hijacked backward.
  // We can't suppress them from the Popup, so on Tab we set them to tabIndex -1:
  // the browser then skips both the guards and the (tabIndex -1) calendar and
  // lands on the real next/previous field. No preventDefault — the browser moves
  // focus, and base-ui's focus-out handler closes the popover. The selector hits
  // every base-ui surface on the page, so restore the guards once the Tab move
  // has completed — leaving another surface's guards at tabIndex -1 would
  // silently break its focus trap. Our own guards unmount with the popover, so
  // restoring them is a harmless no-op.
  const neutralizeFocusGuards = () => {
    const guards = Array.from(
      document.querySelectorAll<HTMLElement>('[data-base-ui-focus-guard]')
    ).map((guard) => ({ guard, tabIndex: guard.tabIndex }));
    guards.forEach(({ guard }) => {
      guard.tabIndex = -1;
    });
    requestAnimationFrame(() => {
      guards.forEach(({ guard, tabIndex }) => {
        guard.tabIndex = tabIndex;
      });
    });
  };

  // Runs the pending arrow-key entry focus (see pendingEntryFocus above) the
  // moment the popup mounts.
  const attachCalendarRef = (el: HTMLDivElement | null) => {
    calendarRef.current = el;
    if (!el || !pendingEntryFocus.current) return;
    pendingEntryFocus.current = false;
    getCalendarEntryTarget()?.focus();
  };

  // ArrowDown/ArrowUp from the input opens the calendar (if needed) and steps
  // focus into it instead of moving the text caret. Tab leaves the field
  // natively (see neutralizeFocusGuards).
  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    inputProps.onKeyDown?.(event);
    if (event.defaultPrevented) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (open) {
        getCalendarEntryTarget()?.focus();
      } else {
        pendingEntryFocus.current = true;
        handleOpenChange(true);
      }
      return;
    }
    if (event.key === 'Tab' && open) {
      // Let the browser's native Tab move focus to the next/previous field
      // (guards neutralized, calendar is tabIndex -1), and close the popover
      // ourselves. We don't preventDefault — the browser does the focus move —
      // and finalFocus={false} keeps base-ui from bouncing it back on close.
      neutralizeFocusGuards();
      handleOpenChange(false);
    }
    if (event.key === 'Enter' && open) {
      // Confirm: a complete typed date already committed on change, so just
      // close. (base-ui's trigger keyboard activation, which would otherwise
      // toggle the popover back open, is suppressed for Enter in the onKeyDown
      // composition.) preventDefault so Enter doesn't also submit a host form.
      event.preventDefault();
      handleOpenChange(false);
    }
  };

  // Tab trap inside the popover: cycle prev → header → next → content (the
  // focused/active grid cell) and wrap, never leaving the calendar. The
  // calendar's controls aren't real tab stops (tabIndex={-1}), so we drive the
  // ring by hand; arrows handle in-grid movement, Esc closes back to the input.
  const contentCellSelector =
    '[data-testid="datepicker-day"],[data-testid="datepicker-month"],[data-testid="datepicker-year"]';
  const handlePopupKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab') return;
    const root = calendarRef.current;
    if (!root) return;
    event.preventDefault();
    const headerButtons = ['datepicker-prev', 'datepicker-header', 'datepicker-next']
      .map((id) => root.querySelector<HTMLElement>(`[data-testid="${id}"]`))
      // The header is a non-focusable label (not a button) in year view.
      .filter((el): el is HTMLElement => el != null && el.tagName === 'BUTTON');
    const active = document.activeElement as HTMLElement | null;
    const inContent = !!active?.matches?.(contentCellSelector);
    if (inContent) lastContentCellRef.current = active;
    const stops = headerButtons.length + 1; // header buttons + one content stop
    const contentIndex = stops - 1;
    const fromIndex = inContent
      ? contentIndex
      : Math.max(0, headerButtons.indexOf(active as HTMLElement));
    const dir = event.shiftKey ? -1 : 1;
    const nextIndex = (fromIndex + dir + stops) % stops;
    if (nextIndex === contentIndex) {
      const remembered = lastContentCellRef.current;
      (remembered && root.contains(remembered) ? remembered : getCalendarEntryTarget())?.focus();
    } else {
      headerButtons[nextIndex]?.focus();
    }
  };

  // Format the displayed value in the active locale so textual formats (e.g.
  // 'MMMM yyyy' in the month picker) match the calendar's language instead of
  // always rendering English month names.
  const displayValue = selected
    ? format(selected, dateFormat, { locale: dateFnsLocales[lang] })
    : '';

  // Uncommitted keystrokes (incomplete text, an out-of-range date, or an
  // emptied non-clearable input) live only in the DOM — the committed value is
  // untouched. On blur, force a re-render so the display snaps back to the
  // committed value: MaskedInput resyncs its DOM text to the `value` prop on
  // any re-render where the two differ (see its componentDidUpdate).
  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    // Focus moving into the calendar (arrow-key entry, clicking a day) is
    // still inside the widget — don't surface it as a blur. Forms validate on
    // blur, and mid-pick a required field would otherwise flag as invalid
    // before the user had a chance to choose a date. The real exit blur (Tab
    // away, click elsewhere) still reaches the consumer below.
    if (calendarRef.current?.contains(event.relatedTarget as Node | null)) return;
    inputProps.onBlur?.(event);
    if (event.target.value !== displayValue) forceDisplayResync((n) => n + 1);
  };

  // The input itself is the popover trigger, tagged `role="combobox"`; with the
  // popup's `initialFocus={false}` (below), floating-ui treats it as an
  // "untrapped typeable combobox" and stops rendering the focus guards that
  // otherwise pull Tab into the calendar — so Tab leaves for the next field while
  // arrow keys step in. `triggerProps` carries base-ui's open handlers, aria
  // attributes, and a merged ref; we pull the ref out to apply it as a real `ref`
  // (React 17 ignores a spread `ref`) and compose our handlers on top.
  const renderComboboxInput = (triggerProps: HTMLProps<HTMLInputElement>) => {
    const {
      ref: triggerRef,
      onFocus: baseOnFocus,
      onBlur: baseOnBlur,
      onPointerDown: baseOnPointerDown,
      onKeyDown: baseOnKeyDown,
      ...rest
    } = triggerProps;
    const shared = {
      ...rest,
      role: 'combobox' as const,
      onFocus: (event: FocusEvent<HTMLInputElement>) => {
        baseOnFocus?.(event);
        handleInputFocusWithPassthrough(event);
      },
      // This composed handler lands after both spreads on the input, replacing
      // the consumer's `onBlur` (re-dispatched in handleInputBlur) and, on the
      // typable path, MaskedInput's own no-op blur forwarder.
      onBlur: (event: FocusEvent<HTMLInputElement>) => {
        baseOnBlur?.(event);
        handleInputBlur(event);
      },
      onPointerDown: (event: PointerEvent<HTMLInputElement>) => {
        baseOnPointerDown?.(event);
        handleInputPointerDown(event);
      },
      onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
        // Skip base-ui's trigger handler for Enter: it activates the trigger
        // (toggling the popover open again right after a typed-date commit
        // closed it). handleInputKeyDown confirms-and-closes on Enter instead.
        if (event.key !== 'Enter') baseOnKeyDown?.(event);
        handleInputKeyDown(event);
      },
    };

    if (isReadonlyInput) {
      return (
        <StyledInput
          {...inputProps}
          {...shared}
          ref={mergeRefs([triggerRef ?? null, inputRef])}
          readOnly
          value={displayValue}
          placeholder={placeholder}
          disabled={isDisabled}
          $error={!!error}
          $hasClearButton={hasClearButton}
        />
      );
    }

    return (
      <MaskedInput
        {...inputProps}
        mask={inputMask}
        pipe={autoCorrectionPipe}
        value={displayValue}
        placeholder={placeholder}
        disabled={isDisabled}
        onChange={(e) => handleInputChange(e as ChangeEvent<HTMLInputElement>)}
        render={(
          maskRef: Ref<HTMLInputElement>,
          { onTextChange: _onTextChange, ...renderProps }: MaskedInputRenderProps
        ) => (
          <StyledInput
            {...renderProps}
            {...shared}
            ref={mergeRefs([maskRef, inputRef, triggerRef ?? null])}
            $error={!!error}
            $hasClearButton={hasClearButton}
          />
        )}
      />
    );
  };

  return (
    <Stack space={1}>
      <LabelWrapper>
        <FormLabel htmlFor={inputId} text={label} icon={labelIcon} />
      </LabelWrapper>
      <FieldDescription text={description} />
      <Popover.Root open={open} onOpenChange={handleOpenChange}>
        {customInput ? (
          // Render the caller's trigger directly as the Trigger so base-ui
          // attaches its props to that element. Wrapping it would produce a
          // second `role="button"` with the same accessible name, which trips
          // Playwright strict mode and confuses assistive tech.
          <Popover.Trigger disabled={isDisabled} render={customInput} />
        ) : (
          // The input itself is the trigger; the wrapper is just a positioning
          // context so the trailing icon can overlay the input's right edge.
          <RelativeWrapper>
            <Popover.Trigger
              nativeButton={false}
              disabled={isDisabled}
              // Use the consumer's `id` (or a generated fallback) as the
              // trigger id. base-ui otherwise generates its own id for the
              // trigger element, which would override the `id` we forward onto
              // the input — breaking `FormLabel htmlFor`, `[id="…"]` selectors,
              // and tests.
              id={inputId}
              render={renderComboboxInput}
            />
            {renderTrailingIcons()}
          </RelativeWrapper>
        )}
        <Popover.Portal
          // Disable floating-ui's portal focus guards on the typeable path.
          // base-ui detects the untrapped typeable combobox in its FocusManager
          // but doesn't forward that to the FloatingPortal, so the portal's guard
          // still redirects Tab into the calendar. `renderGuards={false}` (a real
          // FloatingPortal prop Popover.Portal spreads through but doesn't type)
          // suppresses them. The customInput (button) path keeps the guards.
          {...({ renderGuards: customInput ? undefined : false } as { renderGuards?: boolean })}
        >
          {/* Transparent full-screen backdrop: an outside click lands here and
              dismisses the calendar without activating the control beneath it.
              The portal attr keeps the sidebar's outside-click handler from also
              closing the sidebar. Typing into the focused input is unaffected. */}
          <CalendarBackdrop {...{ [DATEPICKER_PORTAL_ATTR]: '' }} />
          <CalendarPositioner sideOffset={4} side="bottom" align="start">
            <CalendarPopup
              ref={attachCalendarRef}
              aria-label={calendarDialogLabels[lang]}
              {...{ [DATEPICKER_PORTAL_ATTR]: '' }}
              // Combobox path: don't move focus on open (it's already in the
              // input) — the `initialFocus === false` half of the untrapped
              // typeable combobox (the literal `false` matters: base-ui detects
              // the mode by identity). The customInput (button) path follows
              // the dialog pattern instead: opening focuses the most relevant
              // grid cell, like the APG date picker dialog. Without it base-ui
              // would park focus on the popup root — a div with no key
              // handlers — and, with every control out of the tab order, a
              // keyboard user could do nothing but Esc.
              initialFocus={customInput ? () => getCalendarEntryTarget() ?? true : false}
              // Combobox path: don't let base-ui restore focus on close — we own
              // focus (handleOpenChange returns it to the input when closing from
              // inside the calendar). This stops base-ui bouncing focus back to
              // the input after Tab moves it to the next field.
              finalFocus={customInput ? undefined : false}
              // Trap and cycle Tab within the popover. On the customInput
              // path base-ui's focus guards also exist, but they never engage:
              // this handler preventDefaults Tab before the browser moves
              // focus, so the ring below is the only Tab behavior either way.
              onKeyDown={handlePopupKeyDown}
            >
              {calendar}
            </CalendarPopup>
          </CalendarPositioner>
        </Popover.Portal>
      </Popover.Root>
      <FormError text={error} />
    </Stack>
  );
};

const RelativeWrapper = styled.div({
  position: 'relative',
  width: '100%',
});

// Overlays the input's right edge: the persistent indicator icon (calendar,
// or lock when disabled) sits at the far right, with the clear (X) action to
// its left when there is a value to clear. The area is click-transparent so
// a click on the indicator falls through to the input underneath (focusing
// it and opening the calendar); ClearAction re-enables pointer events for
// the X.
const TrailingArea = styled.span({
  position: 'absolute',
  right: pxToRem(8),
  top: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  color: theme.form.icon.color,
  pointerEvents: 'none',
});

// The clear (X) action is a real IconButton — same hover background, active
// scale, and focus ring as every other icon button. sm (24px) so its box
// matches the 24px indicator beside it.
const ClearAction = styled.span({
  display: 'flex',
  pointerEvents: 'auto',
});

const StyledInput = styled.input<{ $error?: boolean; $hasClearButton?: boolean }>((p) => ({
  height: theme.base.height,
  width: '100%',
  borderRadius: theme.form.border.radius,
  backgroundColor: theme.form.bg.color,
  font: theme.font.regular16,
  borderWidth: theme.form.border.width,
  borderStyle: 'solid',
  borderColor: p.$error ? theme.color.error : theme.form.border.color,
  color: theme.form.value.color,
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  // Keep the text clear of the trailing icons: the indicator occupies 2rem
  // from the right edge (0.5rem inset + 1.5rem icon), the clear (X) button
  // another 2rem (1.5rem button + 0.5rem gap) when present.
  paddingRight: p.$hasClearButton ? pxToRem(64) : pxToRem(32),
  outline: 'none',
  transition: transitionStandard('background-color', 'border-color', 'color'),
  '&:hover:not(:focus):not(:disabled)': {
    backgroundColor: theme.form.bg.colorHover,
  },
  '::placeholder': {
    font: theme.font.regular14,
    color: theme.form.placeholder.color,
  },
  '&:focus': {
    color: theme.form.value.colorFocus,
    backgroundColor: theme.form.bg.colorFocus,
    borderColor: p.$error ? theme.color.error : theme.form.border.colorFocus,
    '::placeholder': {
      color: theme.form.placeholder.colorFocus,
    },
  },
}));
